import { useMemo } from 'react';
import { BrowseResults } from '../../components/BrowseResults';
import { PageHeader } from '../../components/PageHeader';
import { Breadcrumb } from '../../components/Breadcrumb';
import { BatchProgressBar } from '../../components/BatchProgressBar';
import { getRouteParams } from '../../services/angular-bridge';
import { useTagByName, useTagsByIds } from '../../hooks/useProjectTags';
import { useProject } from '../../hooks/useProject';
import { useProjectPageMount } from '../../hooks/useProjectPageMount';
import { useBatchReviewMetrics } from '../../hooks/useReview';

/**
 * BrowseBatchPage — simplified batch review (/home/batch/:batchId[/:reviewState]).
 * The Tag Explorer scoped to a batch: the filter is derived from the batch tag id and
 * the review state, mirroring BrowseController's simplified-review filter:
 *   - (none)       → resources tagged with the batch
 *   - reviewed     → batch AND the "Reviewed" tag
 *   - notReviewed  → batch AND NOT "Reviewed"
 *
 * The nav "active batch" highlight (Angular's browse.batch.selected broadcast) and the
 * simplified-review chrome are deferred (nav is Wave 4; grid chrome is M3).
 */
export function BrowseBatchPage() {
  const { projectId, batchId, reviewState } = getRouteParams();
  const needsReviewedTag = reviewState === 'reviewed' || reviewState === 'notReviewed';
  const { data: reviewedTag, isLoading: loadingReviewedTag } = useTagByName(
    projectId,
    needsReviewedTag ? 'Reviewed' : null
  );
  const { data: project } = useProject(projectId);
  useProjectPageMount(project, 'projects');
  // The batch is a tag; resolve its name for the breadcrumb.
  const { data: batchTags } = useTagsByIds(projectId, [batchId]);
  const batchName = batchTags?.[0]?.name ?? 'Batch';
  const reviewLabel =
    reviewState === 'reviewed' ? 'Reviewed' : reviewState === 'notReviewed' ? 'Not Reviewed' : null;
  const { data: metrics } = useBatchReviewMetrics(projectId, batchId);

  const chrome = (
    <>
      <PageHeader icon="far fa-hdd" title={project?.name ?? 'Project'} />
      <Breadcrumb
        items={[
          { label: 'Projects', href: '/project' },
          { label: project?.name ?? 'Project', href: `/project/${projectId}/collections` },
          { label: 'Batch' },
          { label: batchName },
          ...(reviewLabel ? [{ label: reviewLabel }] : []),
        ]}
      />
      {metrics && <BatchProgressBar reviewed={metrics.reviewed} notReviewed={metrics.notReviewed} />}
    </>
  );

  const filter = useMemo(() => {
    const and = [batchId];
    const not: string[] = [];
    if (reviewState === 'reviewed' && reviewedTag) and.push(reviewedTag.id);
    if (reviewState === 'notReviewed' && reviewedTag) not.push(reviewedTag.id);
    return { size: 1, projectId, tags: { and, or: [], not } };
  }, [projectId, batchId, reviewState, reviewedTag]);

  // Wait for the Reviewed tag before building a reviewed/notReviewed filter, so we
  // don't briefly show the whole batch with the wrong scope.
  if (needsReviewedTag && loadingReviewedTag) {
    return (
      <div className="container-fluid">
        {chrome}
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {chrome}
      <BrowseResults projectId={projectId} filter={filter} />
    </div>
  );
}
